import React, { useCallback, useEffect, useState } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Box, Button, Grid, TablePagination } from '@material-ui/core'
import SearchBar from 'material-ui-search-bar'
import { debounce } from 'lodash'
import { DrillCard, LoadingSpinner, NotFound } from 'components'
import { Translate } from 'react-localize-redux'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import PropTypes from 'prop-types'
import Source from './components/Source'
import {
  duplicateDrill,
  fetchClubAgeClasses,
  fetchClubDrillCategories,
  fetchDrillProviders,
  fetchFavouriteDrills,
  fetchTeamDrills,
  updateFavoriteDrill,
} from './api'
import CategoriesDialog from './components/CategoriesDialog'
import { MenuPaper, useStyles } from './styles'
import { DRILL_EDIT_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const ActivitiesContainer = ({
  history,
  team,
  language,
  match: {
    // for now using seasonId from match params because seasons in store have a different structure
    params: { seasonId },
  },
  withPhaseMenu,
  showAddToTrainingButton,
  ageClassLocked,
  onPhaseSelect,
  showDrillActions,
}) => {
  const classes = useStyles()
  const [drillProviders, setDrillProviders] = useState([])
  const [providers, setProviders] = useState([])
  const [ageClasses, setAgeClasses] = useState([])
  const [selectedAgeClass, setSelectedAgeClass] = useState('')
  const [selectedSource, setSelectedSource] = useState('')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [drillCategories, setDrillCategories] = useState([])
  const [drills, setDrills] = useState([])
  const [meta, setMeta] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [rowsPerPage, setRowsPerPage] = useState(12)
  const [page, setPage] = useState(0)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasActivitiesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.activities_enabled
  )
  const { enabled: hasCreateActivitiesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.create_activities
  )

  const checkAndSetAgeClass = useCallback(() => {
    const isTeamAgeClass = ageClasses.some(
      (ageClass) => ageClass.uuid === team.relationships.age_class.uuid
    )
    setSelectedAgeClass(
      isTeamAgeClass ? team.relationships.age_class.uuid : 'all'
    )
  }, [ageClasses, team.relationships.age_class.uuid])

  useEffect(() => {
    if (ageClasses.length > 0) {
      checkAndSetAgeClass()
    }
  }, [ageClasses])

  const fetchDrills = async (filters) => {
    try {
      let response = null
      const params = {
        ...filters,
        per_page: rowsPerPage,
        page: page + 1,
      }
      if (selectedSource === 'my-team-activities') {
        response = await fetchTeamDrills(team.uuid, params)
      } else if (selectedSource === 'my-favourites') {
        response = await fetchFavouriteDrills(team.uuid, params)
      } else {
        response = await fetchTeamDrills(team.uuid, {
          ...params,
          provider_club_uuid: selectedSource,
        })
      }
      setDrills(response.data)
      setMeta(response.meta)
    } catch (error) {
      console.error('Error fetching drills:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDrillProviders(team.uuid).then((res) => {
      setDrillProviders(res.data)
      setSelectedSource(res.data[0].uuid)
    })
  }, [])

  useEffect(() => {
    if (drillProviders.length > 0) {
      const validProviders = drillProviders.filter(
        (provider) => provider.uuid !== undefined
      )
      const modifiedProviders = [
        ...validProviders,
        {
          name: <Translate id="drills.my-drills" />,
          uuid: 'my-team-activities',
        },
        {
          name: <Translate id="drills.my-favourites" />,
          uuid: 'my-favourites',
        },
      ]
      setProviders(modifiedProviders)
    }
  }, [drillProviders])

  useEffect(() => {
    if (
      selectedSource &&
      selectedSource !== 'my-team-activities' &&
      selectedSource !== 'my-favourites'
    ) {
      fetchClubAgeClasses(selectedSource)
        .then((res) => {
          setAgeClasses([{ name: 'All', uuid: 'all' }, ...res.data])
        })
        .catch((error) => {
          console.error('Error fetching age classes:', error)
        })
      fetchClubDrillCategories(selectedSource, { include: 'children.children' })
        .then((res) => {
          setDrillCategories(res.data)
        })
        .catch((error) => {
          console.error('Error fetching drill categories:', error)
        })
    }
  }, [selectedSource])

  useEffect(() => {
    setLoading(true)
    const filters = {}
    if (
      selectedSource &&
      selectedSource !== 'my-team-activities' &&
      selectedSource !== 'my-favourites'
    ) {
      if (selectedAgeClass && selectedAgeClass !== 'all') {
        filters['filter[age_class]'] = selectedAgeClass
      }
      if (selectedCategories.length > 0) {
        filters['filter[categories]'] = selectedCategories.join(',')
      }
    }
    if (searchQuery) {
      filters['filter[search]'] = searchQuery
    }
    if (
      selectedSource &&
      (selectedAgeClass !== '' ||
        selectedSource === 'my-favourites' ||
        selectedSource === 'my-team-activities')
    ) {
      fetchDrills(filters)
    }
  }, [
    selectedSource,
    selectedAgeClass,
    selectedCategories,
    searchQuery,
    rowsPerPage,
    page,
  ])

  const handleSearchChange = useCallback(
    debounce((newValue) => {
      setSearchQuery(newValue)
      setPage(0)
    }, 400),
    []
  )

  const handleSourceChange = (event) => {
    if (featureGuard(hasActivitiesEnabled)) return

    setSelectedSource(event.target.value)
    setSelectedAgeClass(team.relationships.age_class.uuid)
    setSelectedCategories([])
    setSearchQuery('')
    setPage(0)
    setRowsPerPage(12)
  }

  const handleAgeClassChange = (event) => {
    setSelectedAgeClass(event.target.value)
    setSearchQuery('')
    setPage(0)
    setRowsPerPage(12)
  }

  const handleCategoryChange = (categories) => {
    setSelectedCategories(categories)
    setPage(0)
    setRowsPerPage(12)
  }

  const handleReset = () => {
    checkAndSetAgeClass()
    setSelectedCategories([])
    setSearchQuery('')
    setPage(0)
    setRowsPerPage(12)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleFavoriteToggle = (drill) => {
    updateFavoriteDrill(drill.uuid, !drill.favorite)
      .then(() => {
        setDrills((prevDrills) =>
          prevDrills.map((d) =>
            d.uuid === drill.uuid ? { ...d, favorite: !drill.favorite } : d
          )
        )
      })
      .catch((error) => {
        console.error('Error toggling favorite status:', error)
      })
  }

  const openCategoriesDialog = () => {
    if (featureGuard(hasActivitiesEnabled)) return

    setIsDialogOpen(true)
  }

  const closeCategoriesDialog = () => {
    setIsDialogOpen(false)
  }

  const handleDuplicateDrill = (uuid) => {
    if (featureGuard(hasCreateActivitiesEnabled)) return

    duplicateDrill(uuid, team.id)
      .then((res) => {
        const resData = res.data
        if (resData && resData.uuid) {
          const editRoute = DRILL_EDIT_ROUTE.populateURLParams({
            drillUuid: resData.uuid,
          })
          const path = `/team/${team.uuid}/season/${+seasonId}${editRoute}`
          history.push(path)
        }
      })
      .catch((error) => {
        toast.error(<Translate id="errors.drills.duplicate" />)
        console.error('Error duplicating drill:', error)
      })
  }

  return (
    <>
      <Box className={classes.container}>
        {isDialogOpen && (
          <CategoriesDialog
            isOpen={isDialogOpen}
            onClose={closeCategoriesDialog}
            categories={drillCategories}
            initialSelectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
          />
        )}
        <Box className={classes.filtersContainer}>
          <Source
            selectedValue={selectedSource}
            options={providers}
            onChange={handleSourceChange}
            label={<Translate id="drills.source" />}
            labelId="source-label"
            IconComponent={
              !hasActivitiesEnabled
                ? (props) => (
                    <LockIconComponent
                      context="select"
                      style={{ transform: 'none', transition: 'none' }} // Prevent lock icon rotation
                      {...props}
                    />
                  )
                : ArrowDropDownIcon
            }
          />
          {selectedSource !== 'my-team-activities' &&
            selectedSource !== 'my-favourites' && (
              <>
                <Source
                  selectedValue={selectedAgeClass}
                  options={ageClasses}
                  onChange={handleAgeClassChange}
                  label={<Translate id="team.age-class" />}
                  labelId="age-class-label"
                  disabled={ageClassLocked}
                />
                <Button
                  variant="contained"
                  onClick={openCategoriesDialog}
                  endIcon={
                    !hasActivitiesEnabled ? (
                      <LockIconComponent context="secondaryButton" />
                    ) : null
                  }
                  className={classes.categoriesButton}
                >
                  <Translate id="articles.categories" />
                </Button>
                <Button onClick={handleReset}>
                  <Translate id="button.reset" />
                </Button>
              </>
            )}
        </Box>
        <Box className={classes.searchBarContainer}>
          <Grid container spacing={2} direction="row" alignItems="center">
            <Grid item xs={12} md={6} sm={3}>
              <Translate>
                {({ translate }) => (
                  <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onCancelSearch={() => {
                      setSearchQuery('')
                      handleSearchChange('')
                      setPage(0)
                    }}
                    placeholder={translate('drills.search')}
                    className={classes.searchBar}
                  />
                )}
              </Translate>
            </Grid>
            <Grid item xs={12} md={6} sm={9}>
              <TablePagination
                labelRowsPerPage={<Translate id="drills.rows-perpage" />}
                component="div"
                count={meta?.total ?? 0}
                page={page}
                className={classes.tablePagination}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[12, 24, 36]}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      component: MenuPaper,
                      elevation: 0,
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Grid container spacing={3}>
          {drills.length > 0 ? (
            drills.map((drill) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={drill.uuid}>
                <DrillCard
                  drill={drill}
                  onFavoriteToggle={() => handleFavoriteToggle(drill)}
                  onDuplicate={() => handleDuplicateDrill(drill.uuid)}
                  withPhaseMenu={withPhaseMenu}
                  showAddToTrainingButton={showAddToTrainingButton}
                  language={language}
                  onPhaseSelect={onPhaseSelect}
                  showDrillActions={showDrillActions}
                />
              </Grid>
            ))
          ) : (
            <NotFound />
          )}
        </Grid>
      )}
    </>
  )
}

ActivitiesContainer.defaultProps = {
  withPhaseMenu: false,
  showAddToTrainingButton: false,
  showDrillActions: false,
  onPhaseSelect: () => {},
}

ActivitiesContainer.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  team: PropTypes.shape({
    id: PropTypes.number.isRequired,
    uuid: PropTypes.string.isRequired,
    relationships: PropTypes.shape({
      age_class: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  language: PropTypes.string.isRequired,
  ageClassLocked: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      seasonId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  withPhaseMenu: PropTypes.bool,
  showAddToTrainingButton: PropTypes.bool,
  onPhaseSelect: PropTypes.func,
  showDrillActions: PropTypes.bool,
}

export default compose(
  withRouter,
  connect(({ auth, team, clubs }) => ({
    team,
    language: auth.user.language,
    ageClassLocked: clubs.current.lock_team_age_class,
  }))
)(ActivitiesContainer)
